import { ReactElement, useEffect, useState } from 'react';

export type OptionDefinition = {
  label: string;
  value: string;
  /** ラジオボタンを非活性 */
  disabled?: boolean;
};

type Props = {
  options: OptionDefinition[]; // ラジオボタンの選択肢
  isMFormItem?: boolean; // true: ラジオボタンを'm-form__item'として定義
  toBeHidden?: boolean; // true: 選択肢が1つの場合は非表示
  current?: string; // 現在値を上書きする場合に設定
  setFunction: (value: string) => void; // 選択した値をセットする関数
  disabled?: boolean; // ラジオボタンを非活性
};

/**
 * 選択肢定義から共通ラジオボタン UI を構築するコンポーネント。
 * @param props 選択肢、初期値、非表示条件、変更通知関数
 * @returns ラジオボタン群
 */
const RadioButton = ({
  options,
  isMFormItem,
  toBeHidden,
  current,
  setFunction,
  disabled,
}: Props) => {
  const [currentValue, setCurrentValue] = useState<string>(
    current !== undefined ? current : options[0].value
  );

  // 内部 state と呼び出し元 state を同時更新し、見た目と業務状態を一致させる。
  const setSelectedValue = (selectedValue: string): void => {
    setFunction(selectedValue);
    setCurrentValue(selectedValue);
  };

  useEffect(() => {
    // 初期表示時にも現在値を呼び出し元へ通知し、未操作でも状態が確定するようにする。
    setFunction(currentValue);
  }, []);

  const radioComponent = (): React.JSX.Element => {
    return (
      <ul className='m-form-radio'>
        {/* 選択肢定義をそのまま描画し、個別画面側でマークアップを重複させない。 */}
        {options.map((option: OptionDefinition): ReactElement => {
          return (
            <li key={option.value} className='m-form-radio__item'>
              <label>
                <input
                  type='radio'
                  className='m-form-radio__input'
                  name={option.value}
                  value={option.value}
                  checked={option.value === currentValue}
                  onChange={(): void => {
                    setSelectedValue(option.value);
                  }}
                  disabled={disabled || option.disabled === true}
                />
                {option.label}
              </label>
            </li>
          );
        })}
      </ul>
    );
  };

  const hiddenStyle = () => {
    // 選択肢が1件しかない場面では UI ノイズを減らすため非表示にできるようにする。
    if (toBeHidden === true && options.length <= 1) {
      return {
        display: 'none',
      };
    }
    return {};
  };

  return isMFormItem ? (
    <div className='m-form__item' data-testid='radioButton' style={hiddenStyle()}>
      {radioComponent()}
    </div>
  ) : (
    radioComponent()
  );
};

RadioButton.defaultProps = {
  isMFormItem: false,
  toBeHidden: false,
  current: undefined,
  disabled: false, // デフォルト値を設定
};

export default RadioButton;
